import { useRef } from 'react'
import { type ProfileData } from '#types/database.types'

export interface ProfileClusterModalProps {
	profiles: ProfileData[]
	isOpen: boolean
	onClose: () => void
	onProfileClick?: (profile: ProfileData) => void
}

function getClusterCity(profiles: ProfileData[]): string {
	if (!profiles.length) return 'this area'
	const firstCity = profiles[0]?.city_name
	if (
		profiles.every(
			(profile) => profile.city_name && profile.city_name === firstCity,
		)
	) {
		return firstCity || 'this area'
	}
	return 'this area'
}

export function ProfileClusterModal({
	profiles,
	isOpen,
	onClose,
	onProfileClick,
}: ProfileClusterModalProps) {
	const dialogRef = useRef<HTMLDivElement>(null)

	if (!isOpen) return null

	const clusterCity = getClusterCity(profiles)

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center"
			style={{
				background: 'rgba(0, 0, 0, 0.7)',
				backdropFilter: 'blur(4px)',
			}}
		>
			<div
				ref={dialogRef}
				className="relative max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-purple-500/30 bg-black/95 shadow-2xl"
				style={{
					backdropFilter: 'blur(16px)',
				}}
			>
				{/* Header */}
				<div className="flex items-center justify-between border-b border-purple-500/20 p-6">
					<div>
						<h2 className="text-xl font-semibold text-white">
							Profiles in this location
						</h2>
						<p className="mt-1 text-sm text-gray-400">
							{profiles.length} indie{' '}
							{profiles.length === 1 ? 'hacker' : 'hackers'} in {clusterCity}
						</p>
					</div>
					<button
						onClick={onClose}
						className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-purple-500/10 hover:text-white"
						aria-label="Close modal"
					>
						<svg
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Profile grid */}
				<div
					className="overflow-y-auto p-6"
					style={{ maxHeight: 'calc(80vh - 120px)' }}
				>
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
						{profiles.map((profile) => (
							<button
								key={profile.x_handle}
								onClick={() => onProfileClick?.(profile)}
								className="group flex flex-col items-center gap-2 rounded-lg p-3 transition-all hover:bg-purple-500/10"
							>
								<div
									className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-purple-500/40 bg-gray-900 transition-all group-hover:scale-110 group-hover:border-purple-400"
									style={{
										boxShadow: '0 0 0 rgba(139, 92, 246, 0)',
									}}
								>
									<img
										src={profile.avatar_url || ''}
										alt={profile.x_handle}
										className="h-full w-full object-cover"
										onError={(e) => {
											const target = e.target as HTMLImageElement
											target.src =
												'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2264%22 height=%2264%22%3E%3Crect width=%2264%22 height=%2264%22 fill=%22%238b5cf6%22/%3E%3C/svg%3E'
										}}
									/>
								</div>
								<div className="text-center">
									<p className="truncate text-sm font-medium text-white">
										@{profile.x_handle}
									</p>
								</div>
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
